import { Field, Flex, Image, Input, InputGroup, Text } from '@chakra-ui/react'
import { FormErrors } from 'commons/components/layout/FormErrors'
import { Label } from 'commons/components/layout/Label'
import { Section } from 'commons/components/layout/Section'
import { PlacesAutocompleteField } from 'commons/components/modal/PlacesAutocompleteField'
import { isXpayType, PaymentTypes } from 'commons/constants/paymentTypes'
import { BgColors, TextColors } from 'commons/design-system/tokens'
import { FormStep } from 'commons/types/paymentTypes'
import { useMemo, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'

type VerifyIdentityFieldsProps = {
	firstname: string
	lastname: string
	birthdate: string
	city?: string
	zipcode?: string
	country?: string
	step: FormStep
	errors: {
		firstname?: string
		lastname?: string
		birthdate?: string
		zipcode?: string
		city?: string
		country?: string
	}
	isLoading?: boolean
	isReadOnly?: boolean
	onFirstnameChange?: (value: string) => void
	onLastnameChange?: (value: string) => void
	onBirthdateChange?: (value: string) => void
	onCityChange?: (value: string) => void
	onCountryChange?: (value: string) => void
	onZipcodeChange?: (value: string) => void
	onClearError?: (
		field: 'firstname' | 'lastname' | 'birthdate' | 'zipcode' | 'city' | 'country'
	) => void
	isManualMode?: boolean
	onManualModeChange?: (isManual: boolean) => void
	paymentType: PaymentTypes
	source: string
	hideZipAndCity?: boolean
}

export const VerifyIdentityFields = ({
	firstname,
	lastname,
	birthdate,
	city,
	zipcode,
	country,
	step,
	errors,
	isLoading = false,
	isReadOnly = false,
	onFirstnameChange,
	onLastnameChange,
	onBirthdateChange,
	onCityChange,
	onCountryChange,
	onZipcodeChange,
	onClearError,
	isManualMode,
	onManualModeChange,
	paymentType,
	source,
	hideZipAndCity = false
}: VerifyIdentityFieldsProps) => {
	const { t } = useTranslation()

	const getBorder = (isError: boolean) =>
		isError ? `1px solid ${TextColors.STATUS_ERROR}` : `1px solid ${BgColors.Z3}`

	const title = useMemo(() => {
		if (paymentType === PaymentTypes.CARD) {
			return t('phone_form.verify_identity_firstname_title_cb')
		} else if (paymentType === PaymentTypes.XPAY_GOOGLE) {
			return t('phone_form.verify_identity_firstname_title_google_pay')
		} else if (paymentType === PaymentTypes.XPAY_APPLE) {
			return t('phone_form.verify_identity_firstname_title_apple_pay')
		} else return t('phone_form.verify_identity_firstname_title_x_pay')
	}, [paymentType])

	const assetsBaseUrl =
		source === 'money_pot'
			? `${process.env.REACT_APP_POTS_URL}collect/assets/img/payment`
			: `${process.env.REACT_APP_PAYMENT_FORM_URL}l/assets/img/payment`

	const ckoFlowImage = useMemo(() => {
		if (paymentType === PaymentTypes.CARD) {
			return `${assetsBaseUrl}/card-payment-methods.png`
		} else if (paymentType === PaymentTypes.XPAY_GOOGLE) {
			return `${assetsBaseUrl}/gpay.png`
		} else if (paymentType === PaymentTypes.XPAY_APPLE) {
			return `${assetsBaseUrl}/applepay.png`
		} else return `${assetsBaseUrl}/cko_flow.png`
	}, [paymentType, assetsBaseUrl])

	return (
		<>
			<Flex alignItems='center' justify='flex-start' gap={2}>
				<Text fontSize='lg' fontWeight='bold' color={TextColors.PRIMARY}>
					{title}
				</Text>
				<Image src={ckoFlowImage} h='20px' w='auto' alt='Cko Payment Methods' />
			</Flex>
			{/* First Name Field */}
			<Field.Root invalid={!!errors.firstname}>
				<Section px={0} pt={2} pb={0}>
					<Label>{t(`phone_form.verify_identity_firstname_label`)}</Label>
					<InputGroup>
						<Input
							autoFocus
							autoComplete='given-name'
							fontFamily="'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif"
							fontWeight={500}
							type='text'
							mt={2}
							border={getBorder(!!errors.firstname)}
							borderRadius='6px'
							py={2}
							value={firstname}
							onChange={(e: ChangeEvent<HTMLInputElement>) => {
								onFirstnameChange?.(e.target.value)
								if (errors.firstname) {
									onClearError?.('firstname')
								}
							}}
							_focus={{ outline: 'none', borderColor: 'blue.500', boxShadow: 'none' }}
							_focusVisible={{ outline: 'none', borderColor: 'blue.500', boxShadow: 'none' }}
							disabled={isLoading || step === FormStep.STEP_BIRTHDATE || isReadOnly}
							readOnly={isReadOnly}
							bg={isReadOnly ? BgColors.Z4 : BgColors.Z5}
						/>
					</InputGroup>
					{errors.firstname && !isReadOnly && (
						<FormErrors withoutBorder errors={[errors.firstname]} />
					)}
				</Section>
			</Field.Root>
			{/* Last Name Field */}
			<Field.Root invalid={!!errors.lastname}>
				<Section px={0} pt={2} pb={0}>
					<Label>{t(`phone_form.verify_identity_lastname_label`)}</Label>
					<InputGroup>
						<Input
							autoComplete='family-name'
							fontFamily="'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif"
							fontWeight={500}
							type='text'
							mt={2}
							border={getBorder(!!errors.lastname)}
							borderRadius='6px'
							py={2}
							value={lastname}
							onChange={(e: ChangeEvent<HTMLInputElement>) => {
								onLastnameChange?.(e.target.value)
								if (errors.lastname) {
									onClearError?.('lastname')
								}
							}}
							_focus={{ outline: 'none', borderColor: 'blue.500', boxShadow: 'none' }}
							_focusVisible={{ outline: 'none', borderColor: 'blue.500', boxShadow: 'none' }}
							disabled={isLoading || step === FormStep.STEP_BIRTHDATE || isReadOnly}
							readOnly={isReadOnly}
							bg={isReadOnly ? BgColors.Z4 : BgColors.Z5}
						/>
					</InputGroup>
					{errors.lastname && !isReadOnly && (
						<FormErrors withoutBorder errors={[errors.lastname]} />
					)}
				</Section>
			</Field.Root>
			{/* Birthdate Field - shown only in step 2 or if filled in readonly mode */}
			{((step === FormStep.STEP_BIRTHDATE && !isReadOnly) ||
				(isReadOnly && step === FormStep.STEP_BIRTHDATE && birthdate)) && (
				<Field.Root invalid={!!errors.birthdate}>
					<Section px={0} pt={2} pb={0}>
						<Label>{t('phone_form.verify_identity_birthdate_label')}</Label>
						<InputGroup>
							<Input
								autoComplete='bday'
								fontFamily="'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif"
								fontWeight={500}
								type='date'
								mt={isReadOnly ? 2 : 0}
								border={getBorder(!!errors.birthdate)}
								borderRadius='6px'
								py={2}
								value={birthdate}
								onChange={(e: ChangeEvent<HTMLInputElement>) => {
									onBirthdateChange?.(e.target.value)
									if (errors.birthdate) {
										onClearError?.('birthdate')
									}
								}}
								_focus={{ outline: 'none', borderColor: 'blue.500', boxShadow: 'none' }}
								_focusVisible={{ outline: 'none', borderColor: 'blue.500', boxShadow: 'none' }}
								disabled={isLoading || isReadOnly}
								readOnly={isReadOnly}
								bg={isReadOnly ? BgColors.Z4 : BgColors.Z5}
							/>
						</InputGroup>
						{errors.birthdate && !isReadOnly && <FormErrors errors={[errors.birthdate]} />}
					</Section>
				</Field.Root>
			)}
			{isXpayType(paymentType) && (
				<PlacesAutocompleteField
					errors={errors}
					isLoading={isLoading}
					step={step}
					isReadOnly={isReadOnly}
					city={city}
					zipcode={zipcode}
					country={country}
					onZipcodeChange={onZipcodeChange}
					onCityChange={onCityChange}
					onCountryChange={onCountryChange}
					onClearError={onClearError}
					isManualMode={isManualMode}
					onManualModeChange={onManualModeChange}
					label={t(`phone_form.verify_identity_zipcode_label`)}
					hideZipAndCity={hideZipAndCity}
				/>
			)}
		</>
	)
}
